import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useApiQuery } from '../../../../../api/hooks';
import useFairEditState, { FAIR_STATUS } from '../../../../../hooks/useFairEditState';
import { teamColor } from './fairModel';
import { RefreshIcon, PlusIcon, DownloadIcon } from '../../../model/icons';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import TeamHistoryItem from './TeamHistoryItem';
import CentralBankPanel from './CentralBankPanel';
import VenueCard from './VenueCard';
import PointCard from './PointCard';
import PointModal from './modals/PointModal';
import VenueModal from './modals/VenueModal';
import PointTxModal from './modals/PointTxModal';
import '../games.css';
import './fair.css';

const FAIR_STATUSES = [
    { value: FAIR_STATUS.NOT_STARTED, label: 'Не начата', tone: 'dev' },
    { value: FAIR_STATUS.DEVELOP, label: 'Подготовка', tone: 'wait' },
    { value: FAIR_STATUS.ACTIVE, label: 'Активна', tone: 'active' },
    { value: FAIR_STATUS.FINISHED, label: 'Завершена', tone: 'done' }
];

const TABS = [
    { key: 'teams', label: 'Команды' },
    { key: 'bank', label: 'Центральный банк' },
    { key: 'venues', label: 'Площадки' }
];

// Игра «Ярмарка»: head-card (инфо + статус) + табы команды/банк/площадки.
export default function FairGame({ shift }) {
    const smenaId = shift.id;
    const { data: fair, isLoading, error } = useApiQuery(
        ['fair', 'games', smenaId],
        `/api/fair/admin/game/find-by-smena?smenaId=${smenaId}`,
        { enabled: !!smenaId, refetchOnMount: 'always' }
    );

    if (error) return <div className="vp-empty">{error.response?.data?.message || 'Ошибка загрузки игры «Ярмарка»'}</div>;
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!fair) return null;

    return <FairContent fair={fair} shift={shift} />;
}

FairGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function FairContent({ fair, shift }) {
    const fairState = useFairEditState(fair.id, fair);
    const [tab, setTab] = useState('teams');
    const [selectedPointId, setSelectedPointId] = useState(null);
    const [venueModal, setVenueModal] = useState(null); // null | {area}
    const [pointTx, setPointTx] = useState(null); // null | point (модалка продаж точки)

    // Стабильные колбэки для мемоизированных PointCard — открытие модалок не перерисовывает сетки QR.
    const openPoint = useCallback((p) => setSelectedPointId(p.id), []);
    const showPointTx = useCallback((p) => setPointTx(p), []);

    const { teams, tradingAreas } = fairState;

    // Цвет команды берём из team.color (бэк отдаёт hex). Fallback на сгенерированный по stateId
    // для системных / удалённых из домена команд, где color == null.
    const colorOf = useMemo(() => {
        const map = new Map();
        teams.forEach(t => map.set(t.id, t.color));
        return (teamStateId) => map.get(teamStateId) || teamColor(teamStateId);
    }, [teams]);

    const teamName = useMemo(() => {
        const map = new Map();
        teams.forEach(t => map.set(t.id, t.teamName));
        return (teamStateId) => map.get(teamStateId) || `ID ${teamStateId}`;
    }, [teams]);

    const allPoints = useMemo(() => tradingAreas.flatMap(a => a.points || []), [tradingAreas]);
    const findPoint = (id) => allPoints.find(p => p.id === id) || fairState.pendingPoints.find(p => p.id === id);

    const pointName = useMemo(() => {
        const map = new Map();
        allPoints.forEach(p => map.set(p.id, p.name));
        return (id) => map.get(id) || `точка #${id}`;
    }, [allPoints]);

    const handleDeleteTeam = (team) => {
        if (window.confirm(`Убрать команду «${team.teamName}» с ярмарки?`)) fairState.deleteTeam(team.id);
    };
    const handleDeleteVenue = (area) => {
        if (window.confirm(`Удалить площадку «${area.name}»? Все точки на ней тоже будут удалены.`)) fairState.deleteTradingArea(area.id);
    };

    const status = fairState.game?.status;
    const totalPoints = allPoints.length;

    const meta = (
        <>
            <span><b>{teams.length}</b> команд</span>
            <span className="dot">·</span>
            <span><b>{tradingAreas.length}</b> площадок</span>
            <span className="dot">·</span>
            <span><b>{totalPoints}</b> точек</span>
        </>
    );

    const selectedPoint = selectedPointId != null ? findPoint(selectedPointId) : null;

    return (
        <>
            <GameHeader
                title={`Ярмарка — ${shift.title || ''}`}
                meta={meta}
                status={<StatusRow statuses={FAIR_STATUSES} current={status} onChange={fairState.updateStatus} />}
            />
            <section className="content white">
                <div className="bt-tabs">
                    {TABS.map(t => (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</a>
                    ))}
                </div>

                {tab === 'teams' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Команды <span className="fr-cnt">({teams.length})</span></h2>
                            <button type="button" className="btn btn-ghost" onClick={fairState.refreshTeams}>
                                <span className="ic"><RefreshIcon /></span>Обновить команды
                            </button>
                        </div>
                        {teams.length > 0 ? (
                            <div>
                                {teams.map(team => (
                                    <TeamHistoryItem
                                        key={team.id}
                                        team={team}
                                        onAddBalance={fairState.addBalance}
                                        onDelete={handleDeleteTeam}
                                        teamName={teamName}
                                        colorOf={colorOf}
                                        pointName={pointName}
                                    />
                                ))}
                            </div>
                        ) : <div className="bt-empty">Нет команд</div>}
                    </div>
                )}

                {tab === 'bank' && (
                    <CentralBankPanel fairState={fairState} teamName={teamName} colorOf={colorOf} />
                )}

                {tab === 'venues' && (
                    <div className="bt-panel">
                        {fairState.pendingPoints.length > 0 && (
                            <section className="fr-card">
                                <div className="ev-head" style={{ marginBottom: 14 }}>
                                    <h2>На модерации <span className="fr-cnt">({fairState.pendingPoints.length})</span></h2>
                                </div>
                                <div className="fr-points">
                                    {[...fairState.pendingPoints].sort((a, b) => a.id - b.id).map((p, index) => (
                                        <PointCard
                                            key={p.id}
                                            point={p}
                                            index={index}
                                            qrBaseUrl={fairState.game?.qrBaseUrl}
                                            onOpen={openPoint}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        <div className="fr-vmain">
                            <h2 className="fr-vtitle">Площадки</h2>
                            <div className="fr-vmain-acts">
                                <button type="button" className="btn btn-primary" onClick={() => setVenueModal({ area: null })}>
                                    <span className="ic"><PlusIcon /></span>Создать площадку
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={fairState.downloadAllQrCodes}>
                                    <span className="ic"><DownloadIcon /></span>Скачать QR коды
                                </button>
                            </div>
                        </div>
                        {tradingAreas.length > 0 ? (
                            tradingAreas.map(area => (
                                <VenueCard
                                    key={area.id}
                                    area={area}
                                    colorOf={colorOf}
                                    qrBaseUrl={fairState.game?.qrBaseUrl}
                                    onEdit={(a) => setVenueModal({ area: a })}
                                    onDelete={handleDeleteVenue}
                                    onAddPoint={(a) => fairState.createPointOnArea(a.id)}
                                    onPointClick={openPoint}
                                    onShowPointTx={showPointTx}
                                />
                            ))
                        ) : <div className="bt-empty">Нет площадок. Создайте первую площадку для размещения точек.</div>}
                    </div>
                )}
            </section>

            {selectedPoint && (
                <PointModal point={selectedPoint} fairState={fairState} onClose={() => setSelectedPointId(null)} />
            )}
            {venueModal && (
                <VenueModal
                    area={venueModal.area}
                    teams={teams}
                    smenaId={fairState.game?.smenaId}
                    fairState={fairState}
                    onClose={() => setVenueModal(null)}
                />
            )}
            {pointTx && (
                <PointTxModal point={pointTx} teamName={teamName} colorOf={colorOf} onClose={() => setPointTx(null)} />
            )}
        </>
    );
}

FairContent.propTypes = {
    fair: PropTypes.object.isRequired,
    shift: PropTypes.shape({ title: PropTypes.string }).isRequired
};
